import React, { useCallback, useMemo, useState } from "react";
import { UnitFormatsProvider } from "@strava/units/react";
import { UNIT_METRIC } from "@strava/units";
import { Map, type LatLng } from "@strava/feature-global-wasm-mre";

import I18n from "../../utils/I18n";
import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";

import BeaconMapContent from "./BeaconMapContent";
import { isFinished } from "./blipClassName";
import useBeaconPolling from "./useBeaconPolling";
import styles from "./BeaconMap.scss";

interface BeaconMapProps {
  beaconId: string;
  initialPolyline: LatLng[];
  initialUpdateTime?: number;
  status: number;
  pollingInterval: number;
}

const BeaconMap = ({
  beaconId,
  initialPolyline,
  initialUpdateTime,
  status: initialStatus,
  pollingInterval,
}: BeaconMapProps) => {
  const { livePolyline, status } = useBeaconPolling({
    beaconId,
    initialPolyline,
    initialStatus,
    initialUpdateTime,
    pollingInterval,
  });

  const [autoCenter, setAutoCenter] = useState(true);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  // Capture the initial polyline once so <Map.Bounds> only frames the camera
  // on mount. Subsequent live updates are passed as livePolyline to the
  // children, which intentionally don't re-frame.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mapPolyline = useMemo(() => initialPolyline, []);

  const currentPosition: LatLng | null = useMemo(
    () => (livePolyline.length > 0 ? livePolyline[livePolyline.length - 1] : null),
    [livePolyline]
  );

  const handleUserInteraction = useCallback(() => {
    setAutoCenter(false);
  }, []);

  const handleRecenter = useCallback(() => {
    setAutoCenter(true);
    setRecenterTrigger((n) => n + 1);
  }, []);

  return (
    <StravaPackageContextProviders>
      <UnitFormatsProvider
        locale={I18n.language()}
        unitSystem={UNIT_METRIC}
      >
        <div className={styles.mapContainer}>
          <Map
            page={process.env.BEACON_ANALYTICS_PAGE}
            category={process.env.BEACON_ANALYTICS_CATEGORY}
            surfaceIdentifier={process.env.BEACON_SURFACE_IDENTIFIER}
          >
            <Map.InitialCameraMode enable3D={false} />
            <Map.Bounds points={mapPolyline} animateSpeed="instant" />
            <BeaconMapContent
              beaconId={beaconId}
              livePolyline={livePolyline}
              currentPosition={currentPosition}
              status={status}
              autoCenter={autoCenter}
              onUserInteraction={handleUserInteraction}
              recenterTrigger={recenterTrigger}
            />
          </Map>

          {!isFinished(status) && currentPosition && (
            <button
              type="button"
              className={styles.recenterBtn}
              onClick={handleRecenter}
              aria-label={I18n.t("strava.beacon.beacon_map.recenter_aria_label")}
              title={I18n.t("strava.beacon.beacon_map.recenter_title")}
            >
              <svg
                className={styles.recenterIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
              >
                <g fill="#FC5200" fillRule="evenodd">
                  <path d="M16.5 11c0-3.038-2.462-5.5-5.5-5.5S5.5 7.962 5.5 11s2.462 5.5 5.5 5.5 5.5-2.462 5.5-5.5zm-10 0c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5-2.015 4.5-4.5 4.5-4.5-2.015-4.5-4.5zm5-10V.5h-1v6h1V1zm0 15v-.5h-1v6h1V16zm9.5-4.5h.5v-1h-6v1H21zm-15 0h.5v-1h-6v1H6z" />
                  <path d="M11 14c1.736 0 3-1.305 3-3s-1.264-3-3-3-3 1.305-3 3 1.264 3 3 3z" />
                </g>
              </svg>
            </button>
          )}
        </div>
      </UnitFormatsProvider>
    </StravaPackageContextProviders>
  );
};

export default BeaconMap;
